// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color, ellipsis, getSystemColor } from "@keller/XDL/Utilities";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    direction = "direction",
    title = "title",
    titleLink = "titleLink",
    links = "links",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: LinkModel[];
    };
    extendedStyles?: LinkListStyles;
}

export interface LinkListStyles {
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    titleLink?: LinkPresentationProps;
    linksWrapper?: InjectableCss;
    horizontalLinksWrapper?: InjectableCss;
    itemWrapper?: InjectableCss;
    itemLink?: LinkPresentationProps;
}

export const linkListStyles: LinkListStyles = {
    linkListWrapper: {
        css: css`
            padding-bottom: 10px;
            max-width: 198px;
        `,
    },
    titleWrapper: {
        css: css`
            margin-bottom: 16px;
            line-height: 0;
        `,
    },
    titleLink: {
        color: "common.white",
        hoverStyle: {
            color: getSystemColor("common.ui5"),
        },
        typographyProps: {
            size: 12,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
                padding-top: 12px;
                position: relative;
                &:after {
                    content: " ";
                    top: 0;
                    left: 0;
                    width: 126px;
                    height: 2px;
                    background: ${color("common.ui11")};
                    position: absolute;
                }
            `,
        },
        css: css`
            &:focus {
                outline: none;
            }
        `,
    },
    itemWrapper: {
        css: css`
            min-width: 140px;
            max-width: 195px;
            width: 100%;
        `,
    },
    itemLink: {
        color: "common.white",
        hoverStyle: {
            color: getSystemColor("common.ui5"),
        },
        typographyProps: {
            size: 12,
            weight: "normal",
            css: css`
                letter-spacing: 0;
                line-height: 15px;
                ${ellipsis(2)}
            `,
        },
        css: css`
            padding: 1px 0 0 0;
            margin-right: 18px;
            margin-bottom: 12px;
            &:focus {
                outline: none;
            }
        `,
    },
};

const styles = linkListStyles;
export const FooterLinkList: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { links, hasMissingCategories } = useGetLinks(fields.links, o => o.fields.destination);
    const titleLink = useGetLink(fields.titleLink);

    if (hasMissingCategories) {
        return null;
    }

    if (fields.direction === "horizontal") {
        return (
            <StyledWrapper
                css={css`
                    display: flex;
                    flex-wrap: wrap;
                    & > div {
                        margin-right: 32px;
                        line-height: 0;
                        &:last-child {
                            margin-right: 0;
                        }
                    }
                `}
            >
                {links.map((link, index) => (
                    <StyledWrapper key={index} {...styles.horizontalLinksWrapper}>
                        {link.url && (
                            <Link
                                {...styles.itemLink}
                                href={link.url}
                                target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                            >
                                {fields.links[index].fields.overriddenTitle || link.title}
                            </Link>
                        )}
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.linkListWrapper}>
            <StyledWrapper {...styles.titleWrapper}>
                {titleLink ? (
                    <Link href={titleLink.url} {...styles.titleLink}>
                        {fields.title || titleLink.title}
                    </Link>
                ) : (
                    <Typography as="h3" {...styles.titleLink?.typographyProps} color={styles.titleLink?.color}>
                        {fields.title}
                    </Typography>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.linksWrapper}>
                {links.map((link, index) => (
                    <StyledWrapper key={index} {...styles.itemWrapper}>
                        {link.url && (
                            <Link
                                {...styles.itemLink}
                                href={link.url}
                                target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                            >
                                {fields.links[index].fields.overriddenTitle || link.title}
                            </Link>
                        )}
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: FooterLinkList,
    definition: {
        group: "Footer",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: item => item.fields.direction !== "horizontal",
            },
            {
                fieldType: "General",
                name: fields.titleLink,
                displayName: "Title Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                isVisible: item => item.fields.direction !== "horizontal",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
